import { useState } from 'react'

import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'
import {
  useColaboradoresActivos,
  useDevolverAMano,
  useHerramientasPendientes,
  useOrdenesActivas,
  useSolicitarTraslado,
} from '@/modules/programacion/useHerramientas'

import type { HerramientaPendiente } from '@/lib/supabase/tipos-bd'
import estilos from './DestinoDeLasHerramientas.module.css'

/**
 * QUÉ PASA CON CADA HERRAMIENTA CUANDO EL TRABAJO TERMINA.
 *
 * Hasta hoy esta pregunta no se hacía en ninguna pantalla. La orden se cerraba
 * sin mirar la herramienta, se quedaba en 'cerrada' esperando una devolución
 * que nadie recordaba, y la herramienta figuraba a nombre de un trabajo que ya
 * no existe. Lo que pasaba de verdad —el técnico se la lleva porque mañana
 * arranca con ella— no se podía registrar, así que se hacía por fuera.
 *
 * Los tres finales, y por qué dos piden permiso y uno no:
 *
 *   · A bodega: la responsabilidad se termina. No pide aprobación.
 *   · A otra orden o a otro técnico: la responsabilidad CAMBIA DE DUEÑO, y eso
 *     no lo decide quien quiere soltarla. Va a gerencia.
 *
 * Mientras un traslado espera decisión la herramienta no admite nada más: se
 * dice en la fila en vez de ofrecer botones que la base va a rechazar.
 */

type Destino = 'orden' | 'tecnico' | 'bodega'

const ROTULO: Record<Destino, string> = {
  orden: 'Pasarla a otra orden',
  tecnico: 'Pasarla a un técnico',
  bodega: 'Devolverla a bodega',
}

export function DestinoDeLasHerramientas({
  ordenTrabajoId,
  puedeRecibirEnBodega,
}: {
  ordenTrabajoId: string
  /** Solo bodega recibe a mano. Al técnico se le ofrecen los otros dos. */
  puedeRecibirEnBodega: boolean
}) {
  const { pendientes, cargando } = useHerramientasPendientes(ordenTrabajoId)

  if (cargando || pendientes.length === 0) return null

  return (
    <section className={estilos.zona}>
      <header className={estilos.cabecera}>
        <h3 className={estilos.titulo}>
          Herramienta sin devolver
          <span className={estilos.cuenta}>{pendientes.length}</span>
        </h3>
        <p className={estilos.pie}>
          El trabajo no queda finalizado hasta que cada una tenga destino.
        </p>
      </header>

      <ul className={estilos.lista}>
        {pendientes.map((h) => (
          <FilaHerramienta
            key={h.prestamo_id}
            herramienta={h}
            ordenTrabajoId={ordenTrabajoId}
            puedeRecibirEnBodega={puedeRecibirEnBodega}
          />
        ))}
      </ul>
    </section>
  )
}

function FilaHerramienta({
  herramienta: h,
  ordenTrabajoId,
  puedeRecibirEnBodega,
}: {
  herramienta: HerramientaPendiente
  ordenTrabajoId: string
  puedeRecibirEnBodega: boolean
}) {
  const [destino, setDestino] = useState<Destino | null>(null)
  const [aDonde, setADonde] = useState('')
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState<string | null>(null)

  const { ordenes } = useOrdenesActivas()
  const { colaboradores } = useColaboradoresActivos()
  const trasladar = useSolicitarTraslado()
  const devolver = useDevolverAMano()

  const trabajando = trasladar.isPending || devolver.isPending

  function cerrar() {
    setDestino(null)
    setADonde('')
    setMotivo('')
    setError(null)
  }

  async function confirmar() {
    setError(null)
    try {
      if (destino === 'bodega') {
        await devolver.mutateAsync({ prestamoId: h.prestamo_id, motivo: motivo.trim() })
      } else {
        await trasladar.mutateAsync({
          prestamoId: h.prestamo_id,
          ordenDestinoId: destino === 'orden' ? aDonde : null,
          colaboradorDestinoId: destino === 'tecnico' ? aDonde : null,
          motivo: motivo.trim(),
        })
      }
      cerrar()
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo registrar.')
    }
  }

  // Trasladar exige elegir a dónde; devolver a mano solo exige el motivo.
  const listo = motivo.trim().length > 0 && (destino === 'bodega' || aDonde !== '')

  return (
    <li className={estilos.fila}>
      <div className={estilos.identidad}>
        <span className={estilos.etiqueta}>{h.etiqueta}</span>
        <span className={estilos.nombre}>{h.herramienta}</span>
        <span className={estilos.dias}>
          {h.dias_afuera === 0
            ? 'salió hoy'
            : `${h.dias_afuera} ${h.dias_afuera === 1 ? 'día' : 'días'} afuera`}
        </span>
      </div>

      {h.traslado_pendiente ? (
        <p className={estilos.esperando}>
          Tiene un traslado esperando la decisión de gerencia. Hasta que la tome, esta herramienta
          no se mueve.
        </p>
      ) : destino === null ? (
        <div className={estilos.opciones}>
          <Boton variante="plano" onClick={() => setDestino('orden')}>
            {ROTULO.orden}
          </Boton>
          <Boton variante="plano" onClick={() => setDestino('tecnico')}>
            {ROTULO.tecnico}
          </Boton>
          {puedeRecibirEnBodega && (
            <Boton variante="plano" onClick={() => setDestino('bodega')}>
              {ROTULO.bodega}
            </Boton>
          )}
        </div>
      ) : (
        <div className={estilos.formulario}>
          {destino === 'orden' && (
            <label className={estilos.campo}>
              <span>¿A qué orden?</span>
              <select value={aDonde} onChange={(e) => setADonde(e.target.value)} autoFocus>
                <option value="">Elija la orden…</option>
                {ordenes
                  .filter((o) => o.id !== ordenTrabajoId)
                  .map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.numero}
                      {o.cliente ? ` · ${o.cliente}` : ''}
                    </option>
                  ))}
              </select>
            </label>
          )}

          {destino === 'tecnico' && (
            <label className={estilos.campo}>
              <span>¿A quién se la queda?</span>
              <select value={aDonde} onChange={(e) => setADonde(e.target.value)} autoFocus>
                <option value="">Elija el técnico…</option>
                {colaboradores.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                    {c.cargo ? ` · ${c.cargo}` : ''}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className={estilos.campo}>
            <span>{destino === 'bodega' ? '¿Por qué se recibe sin escanear?' : '¿Por qué?'}</span>
            <textarea
              rows={2}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              autoFocus={destino === 'bodega'}
              placeholder={
                destino === 'bodega'
                  ? 'Por ejemplo: la trajo el supervisor, el técnico ya se había ido.'
                  : destino === 'tecnico'
                    ? 'Por ejemplo: mañana arranca con ella en la obra de Anserma.'
                    : 'Por ejemplo: la necesitan hoy mismo en esa obra.'
              }
            />
          </label>

          {destino !== 'bodega' && (
            <p className={estilos.nota}>
              Queda pendiente hasta que <b>gerencia</b> lo apruebe. La herramienta sigue a su
              nombre mientras tanto.
            </p>
          )}

          {destino === 'bodega' && (
            <p className={estilos.nota}>
              Lo normal es que el técnico la escanee en bodega. Recibirla así queda marcada como
              manual para siempre.
            </p>
          )}

          {error && <Aviso tipo="error">{error}</Aviso>}

          <div className={estilos.acciones}>
            <Boton
              variante="principal"
              onClick={() => void confirmar()}
              trabajando={trabajando}
              disabled={!listo}
            >
              {destino === 'bodega' ? 'Recibirla en bodega' : 'Pedir la aprobación'}
            </Boton>
            <Boton variante="secundario" onClick={cerrar}>
              Cancelar
            </Boton>
          </div>
        </div>
      )}
    </li>
  )
}
